import * as THREE from 'three';
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { FilmPass } from 'three/examples/jsm/postprocessing/FilmPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

export type BrickAvatar = { id: number; color: string; sizeUnits: number; row: number; step: number };

type FloatingBlock = {
  id: number;
  mesh: THREE.Mesh;
  /** Home position on the ellipsoid, before any orbit rotation. */
  basePos: THREE.Vector3;
  /** Axis (unit) this block orbits around. */
  orbitAxis: THREE.Vector3;
  orbitSpeed: number;
  /** Own-axis spin velocity (rad/sec per axis). */
  spin: THREE.Vector3;
  /** Cumulative orbit angle. */
  angle: number;
  color: THREE.Color;
  /** How many grid units wide the source brick was (1/2/3) — drives block scale. */
  sizeUnits: number;
  /** Sequencer note this brick arms — used for post-death twinkling. */
  row: number;
  step: number;
};

type ShardPiece = {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  angular: THREE.Vector3;
  /** Same Y-biased orbit params as the main blocks — pieces get swept into the swirl. */
  orbitAxis: THREE.Vector3;
  orbitSpeed: number;
};

type FragmentBurst = {
  pieces: ShardPiece[];
  material: THREE.MeshStandardMaterial;
  life: number;
  maxLife: number;
  /** Sequencer note this burst represents — pulseNote() twinkles matching bursts. */
  row: number;
  step: number;
  /** 0..1 twinkle amount; boosted by pulseNote, decayed each frame. */
  twinkle: number;
  /** Randomized per-pulse decay rate so each firing looks slightly different. */
  twinkleDecay: number;
  /** Base emissive intensity so we can boost + restore cleanly. */
  baseEmissive: number;
  /** Base color + emissive so we can lerp back after white-flashing. */
  baseColor: THREE.Color;
  baseEmissiveColor: THREE.Color;
};

/** Ellipsoid the blocks live on. X radius pushed well past the visible
 *  width (~38 world units on desktop) so blocks wrap off the left/right
 *  edges and the whole scene reads as if the camera is inside the swarm
 *  rather than looking at a centered cluster. */
const RADIUS = new THREE.Vector3(32, 3.6, 4.2);

/** Camera sits at z=7.5 looking at origin — nothing may pass this line, or
 *  it flies between the lens and the scene and occludes the whole visualizer.
 *  Blocks/shards get clamped in Z and shards additionally bounce off it. */
const CAMERA_NEAR_Z = 5.0;

const WHITE = new THREE.Color(0xffffff);

/** Number of unique voronoi-ish chunk geometries pre-computed at startup.
 *  Bursts pick random shards from this pool so no two look identical, but we
 *  amortize convex-hull construction to a one-shot cost. */
const SHARD_POOL_SIZE = 22;
/** How many chunks each explosion spawns. */
const SHARDS_PER_BURST = 12;

export class HeaderScene {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly blocks = new Map<number, FloatingBlock>();
  private readonly bursts: FragmentBurst[] = [];
  private readonly shardPool: THREE.BufferGeometry[] = [];
  // Base is a cube — size-1 bricks read square from the front, size-2/3 get
  // scaled on X into proper elongated bricks with the same profile depth.
  private readonly blockGeometry = new THREE.BoxGeometry(0.95, 0.95, 0.95);
  /** Global slow drift so the whole cluster feels alive. */
  private readonly globalTumble = new THREE.Quaternion();
  /** Post-processing pipeline: bloom + film grain for cinematic feel. */
  private readonly composer: EffectComposer;
  private bloomPass!: UnrealBloomPass;
  /** Big colored ARKANOIZ shown only during the song-complete flash. Hidden
   *  outside flashTitle so the visualizer stays uncluttered. */
  private titleMesh: THREE.Mesh | null = null;
  private titleMaterial: THREE.MeshBasicMaterial | null = null;
  private titleTexture: THREE.Texture | null = null;
  private titleLife = 0;
  private titleMaxLife = 0;

  /** Big 3-2-1 countdown mesh. Runs on Start press before gameplay begins. */
  private countdownMesh: THREE.Mesh | null = null;
  private countdownMaterial: THREE.MeshBasicMaterial | null = null;
  private countdownTexture: THREE.CanvasTexture | null = null;
  private countdownCanvas: HTMLCanvasElement | null = null;
  private countdownCtx: CanvasRenderingContext2D | null = null;
  /** 3 → 2 → 1 → 0 (MAKE SOME NOIZ!) → -1 (done). */
  private countdownValue = -1;
  private countdownElapsed = 0; // seconds since current step appeared
  private readonly countdownSecondsPerNumber = 1.0;
  /** MAKE SOME NOIZ! holds briefly, then fades out — the launch fires at the
   *  start of this window, so the card shouldn't linger over the live game. */
  private readonly countdownSecondsForNoiz = 1.4;
  /** Fires when the whole countdown (including the NOIZ card) finishes. */
  private countdownComplete: (() => void) | null = null;
  /** When true, the countdown animation loop is suppressed and the mesh
   *  is shown at full opacity + configured scale — used by Game's header-
   *  edit mode so the player can position the NOIZ card by dragging. */
  private countdownFrozen = false;
  /** Seconds remaining on the unfreeze fade-out. Nonzero right after a
   *  resume so the NOIZ card eases away instead of snapping to hidden. */
  private countdownUnfreezeFade = 0;
  private readonly countdownUnfreezeFadeSeconds = 0.5;
  /** Persistent base scale/offset for the countdown mesh. Modified live in
   *  header-edit mode, applied every frame so the popped-in animation still
   *  overshoots relative to this baseline. */
  private countdownBaseY = -0.491;
  private countdownBaseScale = 1.0;
  /** When set, the step-0 render swaps "MAKE SOME NOIZ!" for this text.
   *  Used by showLevelBanner for the between-round "LEVEL N" reveal. */
  private bannerOverride: string | null = null;

  constructor(canvas: HTMLCanvasElement, options: { showTitle?: boolean } = {}) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    // Lower vertical FOV + pulled-back camera keeps the same visible framing
    // (~5.2 world units of visible height at z=0) while reducing the horizontal
    // fisheye that stretched bricks at the edges of the wide 22:3 header.
    // Horizontal FOV drops from ~135° → ~104°.
    this.camera = new THREE.PerspectiveCamera(20, 1, 0.1, 100);
    this.camera.position.set(0, 0, 14.7);
    this.camera.lookAt(0, 0, 0);

    // Environmental lighting via PMREM-filtered RoomEnvironment: a small
    // procedural room with soft area lights on every wall. Assigned to
    // scene.environment so every PBR material picks up realistic image-
    // based reflections + diffuse contribution, on top of which the key/
    // rim directional lights carve the primary highlight + silhouette.
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();

    // Sky gradient — WebGL clears over the CSS backdrop, so put the top-dark
    // → bottom-light blue gradient into the scene background itself as a
    // small canvas texture. sRGB colorspace so the values render as authored.
    const bgCanvas = document.createElement('canvas');
    bgCanvas.width = 4;
    bgCanvas.height = 256;
    const bgCtx = bgCanvas.getContext('2d');
    if (bgCtx) {
      const grad = bgCtx.createLinearGradient(0, 0, 0, 256);
      grad.addColorStop(0, '#0a041a');
      grad.addColorStop(0.55, '#101d4a');
      grad.addColorStop(1, '#2a7a95');
      bgCtx.fillStyle = grad;
      bgCtx.fillRect(0, 0, 4, 256);
      const bgTex = new THREE.CanvasTexture(bgCanvas);
      bgTex.colorSpace = THREE.SRGBColorSpace;
      this.scene.background = bgTex;
    }

    const key = new THREE.DirectionalLight(0xfff2d8, 1.4);
    key.position.set(6, 8, 5);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x88a4ff, 0.6);
    rim.position.set(-5, -3, -2);
    this.scene.add(rim);

    this.shardPool = this.buildShardPool();

    // Uniform full-frame bloom (no hot-spot pass) + subtle film grain.
    // Reads as soft film-lens haze on every block instead of blowing out
    // the brightest emissives into overpowering glows.
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    // Depth of field disabled — the bokeh blur was smearing most of the
    // scene and producing halo/ring artifacts around bright shards. Keep
    // bloom + film grain only. setBokeh() becomes a no-op via the null
    // check inside it.
    this.bloomPass = new UnrealBloomPass(new THREE.Vector2(512, 128), 0.02, 0.39, 0.88);
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(new FilmPass(0.22, false));
    this.composer.addPass(new OutputPass());

    // Persistent watermark logo — bright on load, eases to baseline dim.
    // Splash instance opts out (showTitle: false) since the splash already
    // shows the full logo in its own DOM card.
    if (options.showTitle !== false) this.ensureTitleMesh();
  }

  private ensureTitleMesh(): void {
    if (this.titleMesh) return;
    const canvas = document.createElement('canvas');
    canvas.width = 2560;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = '380px "Anton", "Impact", "Arial Narrow", sans-serif';
    ctx.textBaseline = 'middle';
    // "ARKA" stays white, "NOIZ" picks up the splash-title palette so the
    // song-complete flash reads as the same logo the player saw on start.
    const letters: Array<[string, string]> = [
      ['A', '#ffffff'], ['R', '#ffffff'], ['K', '#ffffff'], ['A', '#ffffff'],
      ['N', '#ff1e5a'], ['O', '#ff6a1a'], ['I', '#ffd60a'], ['Z', '#b64bff'],
    ];
    const widths = letters.map(([l]) => ctx.measureText(l).width);
    const kerning = 0.92;
    const totalW = widths.reduce((a, b) => a + b, 0) * kerning;
    let x = canvas.width / 2 - totalW / 2;
    const y = canvas.height / 2;
    for (const [letter, color] of letters) {
      ctx.fillStyle = color;
      ctx.fillText(letter, x, y);
      x += ctx.measureText(letter).width * kerning;
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, depthWrite: false, opacity: 0,
    });
    // Matches the countdown plane so the win-flash lands at the same scale
    // as the 3-2-1 numbers the player just saw.
    const geom = new THREE.PlaneGeometry(20, 4);
    const mesh = new THREE.Mesh(geom, mat);
    // Sit a bit below center so the letters visually anchor to the middle
    // of the header instead of drifting toward the top edge.
    mesh.position.set(0, this.countdownBaseY, 0);
    mesh.visible = false;
    this.titleMesh = mesh;
    this.titleMaterial = mat;
    this.titleTexture = tex;
    this.scene.add(mesh);
  }

  /** Precompute a set of unique convex hulls from random points — reads as
   *  irregular voronoi-fractured shards without the runtime cost of a real
   *  voronoi decomposition. Each pool geometry is reused across many bursts. */
  private buildShardPool(): THREE.BufferGeometry[] {
    const pool: THREE.BufferGeometry[] = [];
    for (let i = 0; i < SHARD_POOL_SIZE; i++) {
      // 7-11 random points inside a small volume, biased so hulls are chunky
      // rather than needle-thin. Radius is small so shards read as debris.
      const pointCount = 7 + Math.floor(Math.random() * 5);
      const points: THREE.Vector3[] = [];
      // Chunkier base — bigger half-sizes on all axes so the pool geometries
      // read as thick shards, not thin flakes. Bumped ~1.6× so the debris
      // stays legible on small mobile viewports. Per-instance scale variance
      // (applied in explodeBrick) adds the "range in scale" mix on top.
      const halfSize = new THREE.Vector3(
        0.30 + Math.random() * 0.12,
        0.26 + Math.random() * 0.10,
        0.28 + Math.random() * 0.12,
      );
      for (let p = 0; p < pointCount; p++) {
        points.push(new THREE.Vector3(
          (Math.random() * 2 - 1) * halfSize.x,
          (Math.random() * 2 - 1) * halfSize.y,
          (Math.random() * 2 - 1) * halfSize.z,
        ));
      }
      try {
        const geom = new ConvexGeometry(points);
        geom.computeVertexNormals();
        pool.push(geom);
      } catch {
        // ConvexGeometry can throw if all points are coplanar — retry this slot.
        i--;
      }
    }
    return pool;
  }

  /** Replace the floating avatars with one block per current live brick. */
  syncBricks(bricks: BrickAvatar[]): void {
    for (const block of this.blocks.values()) {
      this.scene.remove(block.mesh);
      (block.mesh.material as THREE.Material).dispose();
    }
    this.blocks.clear();
    // Fresh song = fresh space, wipe any lingering debris from the last run.
    for (const burst of this.bursts) {
      for (const piece of burst.pieces) this.scene.remove(piece.mesh);
      burst.material.dispose();
    }
    this.bursts.length = 0;

    const count = bricks.length;
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < count; i++) {
      const b = bricks[i]!;
      const color = new THREE.Color(b.color);
      const mat = new THREE.MeshStandardMaterial({
        color,
        emissive: color.clone().multiplyScalar(0.35),
        emissiveIntensity: 0.6,
        // Slightly rougher + more metallic so the key/rim lights carve
        // clearer specular gradients across each spinning face.
        roughness: 0.35,
        metalness: 0.35,
      });
      const mesh = new THREE.Mesh(this.blockGeometry, mat);
      // Wider bricks in the grid → longer blocks here. Also nudge Y-scale up a
      // touch on longer blocks so they don't look too thin against the shorts.
      const sizeUnits = Math.max(1, Math.min(3, Math.round(b.sizeUnits)));
      mesh.scale.set(sizeUnits, 1, 1);

      const y = 1 - (i / Math.max(1, count - 1)) * 2;
      const rXY = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = golden * i;
      const jitter = 0.85 + Math.random() * 0.3;
      // Bias X outward with a signed power curve — Fibonacci-sphere sampling
      // packs the equator, which reads as a pile at center. A smaller exponent
      // lifts near-zero values further toward the edges, carving a horizontal
      // gap through the middle so the title stays legible.
      const xRaw = Math.cos(theta);
      const xBiased = Math.sign(xRaw) * Math.pow(Math.abs(xRaw), 0.3);
      const basePos = new THREE.Vector3(
        xBiased * rXY * RADIUS.x * jitter,
        y * RADIUS.y * jitter,
        Math.sin(theta) * rXY * RADIUS.z * jitter,
      );
      mesh.position.copy(basePos);
      mesh.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
      );

      // Strongly Y-biased axis so the cluster reads as a horizontal vortex.
      const orbitAxis = new THREE.Vector3(
        (Math.random() * 2 - 1) * 0.15,
        1,
        (Math.random() * 2 - 1) * 0.15,
      ).normalize();
      // Slower orbit + spin so the cluster reads as a heavier, floatier
      // swarm — big bricks moving fast reads as frenetic; slower feels
      // more deliberate and matches the larger geometry.
      const orbitSpeed = 0.022 + Math.random() * 0.05;
      const spin = new THREE.Vector3(
        (Math.random() * 2 - 1) * 0.28,
        (Math.random() * 2 - 1) * 0.28,
        (Math.random() * 2 - 1) * 0.28,
      );
      this.scene.add(mesh);
      this.blocks.set(b.id, {
        id: b.id,
        mesh,
        basePos,
        orbitAxis,
        orbitSpeed,
        spin,
        angle: Math.random() * Math.PI * 2,
        color,
        sizeUnits,
        row: b.row,
        step: b.step,
      });
    }
  }

  /** Remove that brick's avatar and spawn a burst of irregular convex shards. */
  explodeBrick(id: number): void {
    const block = this.blocks.get(id);
    if (!block) return;
    const origin = block.mesh.position.clone();
    const rotation = block.mesh.rotation.clone();
    const color = block.color;
    this.scene.remove(block.mesh);
    (block.mesh.material as THREE.Material).dispose();
    this.blocks.delete(id);

    const material = new THREE.MeshStandardMaterial({
      color,
      emissive: color.clone().multiplyScalar(0.35),
      emissiveIntensity: 0.55,
      roughness: 0.5,
      metalness: 0.2,
      transparent: true,
      opacity: 1,
    });

    const pieces: ShardPiece[] = [];
    // Wide horizontal spawn spread so a single burst plants shards across a
    // landscape band, not a tight cluster on the block's footprint — combined
    // with the extra X velocity below this turns each explosion into a swath
    // of debris rather than a sphere collapsing near the origin.
    const spreadX = 0.35 * block.sizeUnits + 4 + Math.random() * 3;
    for (let i = 0; i < SHARDS_PER_BURST; i++) {
      const geom = this.shardPool[Math.floor(Math.random() * this.shardPool.length)]!;
      const mesh = new THREE.Mesh(geom, material);
      // Per-instance scale variance — mix of chunky and smaller pieces so
      // each explosion has a natural range instead of uniform shard sizes.
      // Bumped floor so the smallest shards still read on mobile.
      const instanceScale = 0.85 + Math.random() * 1.35;
      mesh.scale.setScalar(instanceScale);
      // Start scattered horizontally so shards read as a landscape spread of
      // debris right from spawn instead of collapsing to a point source.
      mesh.position.copy(origin).add(new THREE.Vector3(
        (Math.random() * 2 - 1) * spreadX,
        (Math.random() * 2 - 1) * 0.14,
        (Math.random() * 2 - 1) * 0.22,
      ));
      mesh.rotation.copy(rotation);
      mesh.rotation.x += Math.random() * 0.6;
      mesh.rotation.y += Math.random() * 0.6;
      mesh.rotation.z += Math.random() * 0.6;
      const dir = mesh.position.clone().sub(origin).normalize();
      // Softer initial burst — earlier speeds (5.5-8, xKick ±18-32) sent
      // shards flying off-screen fast enough to feel violent instead of
      // playful. Halved base + range so bursts pop then drift; the low
      // per-shard orbit still holds the landing spread across the header.
      const speed = 3 + Math.random() * 1.5;
      // Horizontal spread — still asymmetric per shard so bursts don't lean
      // one side over time, but magnitudes now cap around ±14 instead of
      // ±32 so pieces stay on-screen instead of blasting past the edges.
      const xKick = (Math.random() * 2 - 1) * (8 + Math.random() * 6);
      const velocity = dir.multiplyScalar(speed).add(new THREE.Vector3(
        (Math.random() * 2 - 1) * 0.6 + xKick,
        (Math.random() * 2 - 1) * 0.6,
        (Math.random() * 2 - 1) * 0.6,
      ));
      const angular = new THREE.Vector3(
        (Math.random() * 2 - 1) * 2.2,
        (Math.random() * 2 - 1) * 2.2,
        (Math.random() * 2 - 1) * 2.2,
      );
      const orbitAxis = new THREE.Vector3(
        (Math.random() * 2 - 1) * 0.15,
        1,
        (Math.random() * 2 - 1) * 0.15,
      ).normalize();
      // Very small orbit — earlier 0.05-0.14 rad/sec spiraled every burst
      // back toward the Y-axis-through-origin, so all shards eventually piled
      // up center regardless of where they spawned. Near-zero holds the
      // landing spread across the full header width.
      const orbitSpeed = 0.005 + Math.random() * 0.015;
      this.scene.add(mesh);
      pieces.push({ mesh, velocity, angular, orbitAxis, orbitSpeed });
    }

    // Bursts live forever — cleared en masse by syncBricks when a new song
    // starts. Previously aged out after 60s, which made fragments visibly
    // pop out one-by-one on the game-over screen.
    this.bursts.push({
      pieces,
      material,
      life: 0,
      maxLife: Infinity,
      row: block.row,
      step: block.step,
      // Seed twinkle at 1 so shards flash white on spawn and decay back to
      // their own color — same white-glow pulse the paddle + bricks use.
      twinkle: 1,
      twinkleDecay: 0.86,
      baseEmissive: material.emissiveIntensity,
      baseColor: material.color.clone(),
      baseEmissiveColor: material.emissive.clone(),
    });
  }

  /** Called from the sequencer's tick — twinkles every burst whose source
   *  brick lived in the same (row, step) slot so the debris visualizes music.
   *  Each firing gets a random decay so bursts don't all pulse identically. */
  pulseNote(row: number, step: number): void {
    for (const burst of this.bursts) {
      if (burst.row === row && burst.step === step) {
        burst.twinkle = 1;
        burst.twinkleDecay = 0.82 + Math.random() * 0.12;
      }
    }
  }

  /** Kick off a 3 → 2 → 1 → MAKE SOME NOIZ! countdown in the visualizer.
   *  Numbers pop in with a scale + fade, held for ~1s each. `onComplete`
   *  fires once the final NOIZ card fades out — Game uses it to auto-launch
   *  the ball so no click is required. */
  startCountdown(onComplete?: () => void): void {
    if (!this.countdownMesh) {
      // Canvas + plane share a 5:1 aspect wide enough that "MAKE SOME NOIZ!"
      // can render at the same big font size as the numbers without shrinking.
      const canvas = document.createElement('canvas');
      canvas.width = 2560;
      canvas.height = 512;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      this.countdownCanvas = canvas;
      this.countdownCtx = ctx;
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      const mat = new THREE.MeshBasicMaterial({
        map: tex, transparent: true, depthWrite: false, opacity: 0,
      });
      const geom = new THREE.PlaneGeometry(20, 4);
      const mesh = new THREE.Mesh(geom, mat);
      // Position uses countdownBaseY (default -0.3) — tweak live from Game's
      // header-edit mode; the animation loop reads it every frame.
      mesh.position.set(0, this.countdownBaseY, 0.05);
      this.countdownMesh = mesh;
      this.countdownMaterial = mat;
      this.countdownTexture = tex;
      this.scene.add(mesh);
    }
    this.countdownValue = 3;
    this.countdownElapsed = 0;
    this.countdownComplete = onComplete ?? null;
    // Full 3-2-1 countdown always shows the default NOIZ card, not a level
    // banner from a previous round.
    this.bannerOverride = null;
    this.paintCountdown(3);
    if (this.countdownMesh) this.countdownMesh.visible = true;
  }

  /** One-shot pop-in / hold / fade of a custom banner (e.g. "LEVEL 2").
   *  Reuses the countdown mesh + animation and skips the 3-2-1 numbers. */
  showLevelBanner(text: string): void {
    // startCountdown creates the mesh lazily; we call it to guarantee init
    // then override the state to jump straight to the NOIZ-card step.
    this.startCountdown();
    this.bannerOverride = text;
    this.countdownValue = 0;
    this.countdownElapsed = 0;
    this.countdownComplete = null;
    this.paintCountdown(0);
  }

  private paintCountdown(step: number): void {
    if (!this.countdownCtx || !this.countdownCanvas || !this.countdownTexture) return;
    const ctx = this.countdownCtx;
    const w = this.countdownCanvas.width;
    const h = this.countdownCanvas.height;
    ctx.clearRect(0, 0, w, h);
    // Pure white — the header's subtle bloom pass gives it just enough lift
    // to feel alive without haloing into a glow blob.
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Dead-center: canvas center maps to plane center (mesh sits at world Y=0),
    // and textBaseline='middle' anchors the glyph on that line. No pixel fudge.
    const baselineY = h / 2;
    if (step === 0) {
      // Final "MAKE SOME NOIZ!" card — render at the same 380px font as the
      // numbers so the tagline lands with equal visual weight. Falls back to
      // shrinking only if a narrower font swap ever pushes it past the canvas.
      const target = this.bannerOverride ?? 'MAKE SOME NOIZ!';
      const maxWidth = w * 0.95;
      let fontSize = 380;
      ctx.font = `${fontSize}px "Anton", "Impact", "Arial Narrow", sans-serif`;
      while (ctx.measureText(target).width > maxWidth && fontSize > 60) {
        fontSize -= 10;
        ctx.font = `${fontSize}px "Anton", "Impact", "Arial Narrow", sans-serif`;
      }
      ctx.fillText(target, w / 2, baselineY);
    } else {
      ctx.font = '380px "Anton", "Impact", "Arial Narrow", sans-serif';
      ctx.fillText(String(step), w / 2, baselineY);
    }
    this.countdownTexture.needsUpdate = true;
  }

  /** Header-edit mode: pins the "MAKE SOME NOIZ!" card at full opacity so
   *  the player can drag/scale it in place. Call setHeaderEditFrozen(false)
   *  to release. */
  setHeaderEditFrozen(frozen: boolean): void {
    this.countdownFrozen = frozen;
    if (!this.countdownMesh || !this.countdownMaterial) {
      if (!frozen) return;
      // Ensure the mesh exists so freezing works even before the first countdown.
      this.startCountdown();
      // Skip past the numbers straight to the NOIZ card.
      this.countdownValue = 0;
      this.countdownElapsed = 0;
      this.paintCountdown(0);
    }
    if (frozen && this.countdownMesh && this.countdownMaterial) {
      this.paintCountdown(0);
      this.countdownMesh.visible = true;
      this.countdownMaterial.opacity = 1;
      this.countdownMesh.position.y = this.countdownBaseY;
      this.countdownMesh.scale.setScalar(this.countdownBaseScale);
      // Kill any running animation + any lingering fade so it doesn't blink.
      this.countdownValue = -1;
      this.countdownElapsed = 0;
      this.countdownComplete = null;
      this.countdownUnfreezeFade = 0;
    } else if (!frozen && this.countdownMesh && this.countdownMaterial) {
      // Kick off a short fade instead of hiding immediately — the update loop
      // ticks the opacity down and hides the mesh once it hits zero.
      this.countdownUnfreezeFade = this.countdownUnfreezeFadeSeconds;
    }
  }

  /** Live-adjust the countdown/NOIZ mesh position + scale from Game's header-
   *  edit interaction. Values persist across countdown/title runs. */
  setHeaderCardTransform(y: number, scale: number): void {
    this.countdownBaseY = y;
    this.countdownBaseScale = scale;
    if (this.countdownMesh && this.countdownFrozen) {
      this.countdownMesh.position.y = y;
      this.countdownMesh.scale.setScalar(scale);
    }
    if (this.titleMesh) {
      this.titleMesh.position.y = y;
    }
  }

  getHeaderCardTransform(): { y: number; scale: number } {
    return { y: this.countdownBaseY, scale: this.countdownBaseScale };
  }

  /** Live-tune the bloom pass from the settings panel. */
  setBloom(params: Partial<{ strength: number; radius: number; threshold: number }>): void {
    if (!this.bloomPass) return;
    if (params.strength !== undefined) this.bloomPass.strength = params.strength;
    if (params.radius !== undefined) this.bloomPass.radius = params.radius;
    if (params.threshold !== undefined) this.bloomPass.threshold = params.threshold;
  }

  /** Live-tune the depth-of-field pass so the settings panel sliders can
   *  update focus/aperture/maxblur without rebuilding the composer. */
  setBokeh(_params: Partial<{ focus: number; aperture: number; maxblur: number }>): void {
    // No-op — bokeh/DoF pass removed. Kept so Game's settings-panel
    // callbacks don't need to know the pipeline shape.
  }

  /** Song-complete celebration: big colored ARKANOIZ pops in, holds for a
   *  few seconds, then fades out. No lingering watermark. */
  flashTitle(): void {
    this.ensureTitleMesh();
    // If the header-edit mode left the NOIZ card pinned (e.g. user paused
    // and never resumed before winning), tear it down cleanly so the win
    // logo doesn't render on top of it.
    this.countdownFrozen = false;
    this.countdownUnfreezeFade = 0;
    if (this.countdownMesh && this.countdownMaterial) {
      this.countdownMesh.visible = false;
      this.countdownMaterial.opacity = 0;
    }
    this.countdownValue = -1;
    this.countdownElapsed = 0;
    this.countdownComplete = null;

    this.titleLife = 0;
    this.titleMaxLife = 5.0;
    if (this.titleMesh) {
      this.titleMesh.scale.setScalar(0.4);
      this.titleMesh.visible = true;
    }
    if (this.titleMaterial) {
      this.titleMaterial.opacity = 0;
      this.titleMaterial.color.setScalar(1);
    }
  }

  update(delta: number): void {
    this.resizeIfNeeded();

    const globalStep = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0.05, 1, 0.03).normalize(),
      0.012 * delta,
    );
    this.globalTumble.premultiply(globalStep);

    const q = new THREE.Quaternion();
    const workingPos = new THREE.Vector3();
    for (const block of this.blocks.values()) {
      block.angle += block.orbitSpeed * delta;
      q.setFromAxisAngle(block.orbitAxis, block.angle);
      workingPos.copy(block.basePos).applyQuaternion(q).applyQuaternion(this.globalTumble);
      block.mesh.position.copy(workingPos);
      // Camera collision: clamp so no block gets close enough to the lens
      // to occlude the field. Positional-only since blocks ride orbits.
      if (block.mesh.position.z > CAMERA_NEAR_Z) block.mesh.position.z = CAMERA_NEAR_Z;
      block.mesh.rotation.x += block.spin.x * delta;
      block.mesh.rotation.y += block.spin.y * delta;
      block.mesh.rotation.z += block.spin.z * delta;
    }

    // Block-block collision: after the orbit pass computes each block's target
    // position, push any overlapping pair apart so bricks visibly bounce off
    // each other instead of clipping through. Positional-only (blocks are on
    // orbit rails, not free-moving) but the correction persists just long
    // enough for the eye to read the interaction. AABB pre-check keeps the
    // O(n²) sweep cheap at typical block counts (~20).
    const blockList = Array.from(this.blocks.values());
    for (let i = 0; i < blockList.length; i++) {
      const a = blockList[i]!;
      // Block half-width scales with sizeUnits on X; use max dimension as
      // a bounding-sphere radius so wide bricks still collide correctly.
      const rA = 0.31 * Math.max(1, a.sizeUnits) * 1.1;
      const pa = a.mesh.position;
      for (let j = i + 1; j < blockList.length; j++) {
        const b = blockList[j]!;
        const rB = 0.31 * Math.max(1, b.sizeUnits) * 1.1;
        const minDist = rA + rB;
        const minDistSq = minDist * minDist;
        const dx = pa.x - b.mesh.position.x;
        if (dx > minDist || dx < -minDist) continue;
        const dy = pa.y - b.mesh.position.y;
        if (dy > minDist || dy < -minDist) continue;
        const dz = pa.z - b.mesh.position.z;
        if (dz > minDist || dz < -minDist) continue;
        const distSq = dx * dx + dy * dy + dz * dz;
        if (distSq >= minDistSq || distSq === 0) continue;
        const dist = Math.sqrt(distSq);
        const push = (minDist - dist) * 0.5;
        const nx = dx / dist, ny = dy / dist, nz = dz / dist;
        pa.x += nx * push; pa.y += ny * push; pa.z += nz * push;
        b.mesh.position.x -= nx * push;
        b.mesh.position.y -= ny * push;
        b.mesh.position.z -= nz * push;
      }
    }

    // Angular damp stays constant. Linear damp is age-based per burst so
    // fragments decelerate fast right after impact (fast-to-slow "speed
    // ramp" / bullet-time feel), then coast for a long time on the drift.
    const angularDamp = Math.exp(-0.25 * delta);
    const orbitDelta = new THREE.Quaternion();
    for (let f = this.bursts.length - 1; f >= 0; f--) {
      const burst = this.bursts[f]!;
      burst.life += delta;
      if (burst.life >= burst.maxLife) {
        for (const piece of burst.pieces) this.scene.remove(piece.mesh);
        burst.material.dispose();
        this.bursts.splice(f, 1);
        continue;
      }
      // Slow decay for the first ~0.6s so shards actually travel across the
      // header before settling — the previous aggressive early damp killed
      // most of the horizontal velocity within a fraction of a second and
      // left everything piled in the center. After the initial spread the
      // gentle coast holds the landscape in place.
      const damp = burst.life < 0.6
        ? Math.exp(-1.4 * delta)
        : Math.exp(-0.35 * delta);
      const linearDamp = damp;
      // Twinkle: fade toward white (both color + emissive) then decay back.
      // Each pulseNote picks a random decay rate so bursts flicker organically.
      if (burst.twinkle > 0.001) {
        const k = burst.twinkle;
        burst.material.color.copy(burst.baseColor).lerp(WHITE, k * 0.9);
        burst.material.emissive.copy(burst.baseEmissiveColor).lerp(WHITE, k);
        burst.material.emissiveIntensity = burst.baseEmissive + k * 3.5;
        burst.twinkle *= burst.twinkleDecay;
      } else if (burst.material.emissiveIntensity !== burst.baseEmissive) {
        burst.material.color.copy(burst.baseColor);
        burst.material.emissive.copy(burst.baseEmissiveColor);
        burst.material.emissiveIntensity = burst.baseEmissive;
      }
      for (const piece of burst.pieces) {
        piece.mesh.position.addScaledVector(piece.velocity, delta);
        piece.velocity.multiplyScalar(linearDamp);
        // Same per-block-style orbit force: rotate current position around
        // the piece's tilted-toward-Y axis. Combined with the residual burst
        // velocity, pieces flow through the same vortex as the main cluster.
        orbitDelta.setFromAxisAngle(piece.orbitAxis, piece.orbitSpeed * delta);
        piece.mesh.position.applyQuaternion(orbitDelta);
        piece.mesh.position.applyQuaternion(globalStep);
        // Camera collision: shards approaching the lens clamp + bounce back
        // with damping so they can't fly between the camera and the field.
        if (piece.mesh.position.z > CAMERA_NEAR_Z) {
          piece.mesh.position.z = CAMERA_NEAR_Z;
          if (piece.velocity.z > 0) piece.velocity.z *= -0.55;
        }
        piece.mesh.rotation.x += piece.angular.x * delta;
        piece.mesh.rotation.y += piece.angular.y * delta;
        piece.mesh.rotation.z += piece.angular.z * delta;
        piece.angular.multiplyScalar(angularDamp);
      }
    }

    // Piece-piece collision — sphere approximation, equal-mass elastic bounce.
    // Cheap AABB pre-check keeps the O(n²) loop quick until piece counts get
    // huge. Also adds a positional push-apart so overlapping pieces separate.
    const PIECE_R = 0.14;
    const twoR = PIECE_R * 2;
    const twoRSq = twoR * twoR;
    const all: ShardPiece[] = [];
    for (const burst of this.bursts) for (const p of burst.pieces) all.push(p);
    for (let i = 0; i < all.length; i++) {
      const a = all[i]!;
      const pa = a.mesh.position;
      for (let j = i + 1; j < all.length; j++) {
        const b = all[j]!;
        const pb = b.mesh.position;
        const dx = pa.x - pb.x;
        if (dx > twoR || dx < -twoR) continue;
        const dy = pa.y - pb.y;
        if (dy > twoR || dy < -twoR) continue;
        const dz = pa.z - pb.z;
        if (dz > twoR || dz < -twoR) continue;
        const distSq = dx * dx + dy * dy + dz * dz;
        if (distSq >= twoRSq || distSq === 0) continue;
        const dist = Math.sqrt(distSq);
        const nx = dx / dist;
        const ny = dy / dist;
        const nz = dz / dist;
        const push = (twoR - dist) * 0.5;
        pa.x += nx * push; pa.y += ny * push; pa.z += nz * push;
        pb.x -= nx * push; pb.y -= ny * push; pb.z -= nz * push;
        // Exchange velocity along the collision normal (equal mass, 1:1 swap).
        const va = a.velocity.x * nx + a.velocity.y * ny + a.velocity.z * nz;
        const vb = b.velocity.x * nx + b.velocity.y * ny + b.velocity.z * nz;
        const rel = vb - va;
        if (rel < 0) continue; // already separating
        a.velocity.x += rel * nx; a.velocity.y += rel * ny; a.velocity.z += rel * nz;
        b.velocity.x -= rel * nx; b.velocity.y -= rel * ny; b.velocity.z -= rel * nz;
      }
    }

    // Win-title animation: fast pop-in, hold big + full-color, then fade out.
    // Colors on the letters do the celebration — no brightness pumping, so
    // bloom doesn't wash the logo into a white blob.
    if (this.titleMesh && this.titleMaterial && this.titleMaxLife > 0) {
      this.titleLife += delta;
      const t = Math.min(1, this.titleLife / this.titleMaxLife);
      // Scale: rapid ease-out from 0.4 → 1.35 overshoot → settle at 1.15.
      const growPhase = Math.min(1, t / 0.12);
      const scale = 0.4 + (1.35 - 0.4) * (1 - Math.pow(1 - growPhase, 3));
      const settle = t > 0.12 ? 1.35 - (1.35 - 1.15) * Math.min(1, (t - 0.12) / 0.1) : scale;
      this.titleMesh.scale.setScalar(t > 0.12 ? settle : scale);
      // Opacity: fade in / hold at full / fade back out to zero.
      let alpha: number;
      if (t < 0.08) alpha = t / 0.08;
      else if (t > 0.8) alpha = 1 - (t - 0.8) / 0.2;
      else alpha = 1;
      this.titleMaterial.opacity = Math.max(0, alpha);
      if (t >= 1) {
        this.titleMaxLife = 0;
        this.titleMesh.visible = false;
        this.titleMaterial.opacity = 0;
      }
    }

    // Header-edit unfreeze fade: after Game releases the frozen NOIZ card,
    // ease its opacity out over ~0.5s so the resume feels smooth.
    if (this.countdownUnfreezeFade > 0 && this.countdownMesh && this.countdownMaterial) {
      this.countdownUnfreezeFade = Math.max(0, this.countdownUnfreezeFade - delta);
      const t = this.countdownUnfreezeFade / this.countdownUnfreezeFadeSeconds;
      // Ease-out: opacity = t^2 so the tail lingers, matches the NOIZ card fade.
      this.countdownMaterial.opacity = Math.max(0, t * t);
      if (this.countdownUnfreezeFade === 0) {
        this.countdownMesh.visible = false;
        this.countdownMaterial.opacity = 0;
      }
    }

    // 3 → 2 → 1 → MAKE SOME NOIZ! countdown: pop scale + fade per step,
    // then fire the completion callback so Game can auto-launch the ball.
    // Suppressed while frozen so the header-edit interaction owns the mesh.
    if (
      !this.countdownFrozen
      && this.countdownMesh && this.countdownMaterial && this.countdownValue >= 0
    ) {
      this.countdownElapsed += delta;
      const hold = this.countdownValue === 0
        ? this.countdownSecondsForNoiz
        : this.countdownSecondsPerNumber;
      const t = Math.min(1, this.countdownElapsed / hold);
      // Scale: fast overshoot ease from 0.6 → 1.15 → settle at 1.0.
      // Multiplied by countdownBaseScale so a header-edit resize sticks.
      const grow = Math.min(1, t / 0.18);
      const eased = 1 - Math.pow(1 - grow, 3);
      const overshoot = 0.6 + (1.15 - 0.6) * eased;
      const settle = t > 0.18 ? 1.15 - (1.15 - 1.0) * Math.min(1, (t - 0.18) / 0.15) : overshoot;
      const anim = t > 0.18 ? settle : overshoot;
      this.countdownMesh.scale.setScalar(anim * this.countdownBaseScale);
      // Keep the mesh riding the base Y in case it was tweaked mid-countdown.
      this.countdownMesh.position.y = this.countdownBaseY;
      // Opacity: quick fade in, hold, fade out. NOIZ card fades earlier now
      // that the launch fires at the start of its window — the game is live
      // for the whole card, so hanging it up front reads as leftover UI.
      const fadeStart = this.countdownValue === 0 ? 0.15 : 0.75;
      let alpha: number;
      if (t < 0.1) alpha = t / 0.1;
      else if (t > fadeStart) {
        const fadeProgress = (t - fadeStart) / (1 - fadeStart);
        // Ease-out curve so the tail lingers instead of falling linearly.
        alpha = 1 - fadeProgress * fadeProgress;
      } else alpha = 1;
      this.countdownMaterial.opacity = Math.max(0, alpha);
      if (t >= 1) {
        this.countdownValue -= 1;
        this.countdownElapsed = 0;
        if (this.countdownValue === 0) {
          // NOIZ card just appeared — fire the launch callback NOW so the
          // ball drops on the "MAKE SOME NOIZ!" beat, then let the card
          // linger + fade over its own hold time.
          this.paintCountdown(0);
          const cb = this.countdownComplete;
          this.countdownComplete = null;
          cb?.();
        } else if (this.countdownValue > 0) {
          this.paintCountdown(this.countdownValue);
        } else {
          this.countdownMesh.visible = false;
          // Reset any level-banner override so the next default countdown
          // paints "MAKE SOME NOIZ!" rather than the previous banner text.
          this.bannerOverride = null;
        }
      }
    }

    this.composer.render(delta);
  }

  private resizeIfNeeded(): void {
    const canvas = this.renderer.domElement;
    const w = Math.max(1, canvas.clientWidth);
    const h = Math.max(1, canvas.clientHeight);
    if (canvas.width === w && canvas.height === h) return;
    this.renderer.setSize(w, h, false);
    this.composer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    for (const block of this.blocks.values()) {
      (block.mesh.material as THREE.Material).dispose();
    }
    for (const burst of this.bursts) {
      for (const piece of burst.pieces) this.scene.remove(piece.mesh);
      burst.material.dispose();
    }
    for (const geom of this.shardPool) geom.dispose();
    this.blockGeometry.dispose();
    if (this.titleMesh) this.titleMesh.geometry.dispose();
    this.titleMaterial?.dispose();
    this.titleTexture?.dispose();
    if (this.countdownMesh) this.countdownMesh.geometry.dispose();
    this.countdownMaterial?.dispose();
    this.countdownTexture?.dispose();
    this.renderer.dispose();
  }
}
